<template>
  <div class="form-group datetime-responsive" v-on-clickaway="handleDismiss">
    <div class="form-label" v-if="hasLabel">{{ label }}</div>
    <button
      ref="toggleBtn"
      class="dt-output"
      :class="{ active: open }"
      :disabled="disabled"
      @click="handleToggle"
    >
      <div class="dt-output--date">
        <img
          v-if="withIcon"
          class="icon-calendar"
          src="@/assets/media/icons/calendar-v2.svg"
          alt="icon-calendar"
        />
        <span class="dt-output--date__content">{{
          outputDate ? outputDate : output.date
        }}</span>
      </div>
      <span class="dt-output-time">{{
        dateOnly
          ? ""
          : isClearable && label === "Starts" && timePickerDisabled
          ? "Anytime"
          : isClearable && timePickerDisabled
          ? "\n"
          : output.time
      }}</span>
    </button>
    <transition name="fade-up">
      <div
        v-if="open"
        class="dropdown"
        :class="{ top: showToTop }"
        :style="{ right: dropdownPosition === 'right' ? '15px' : null }"
      >
        <div class="datepicker-header" v-if="windowWidth < 640">
          <button class="dropdown-close" @click="handleClose()">
            <img
              class="icon-close"
              src="@/assets/media/icons/icon-close-v2.svg"
              alt="icon-close"
            />
          </button>
        </div>
        <DatePickerLocal
          v-if="!timeOnly"
          v-model="date"
          :local="local"
          :disabledDate="disabledDates"
          :disabledPreviousMonth="disabledPreviousMonth"
          :markedDateToday="markedDateToday"
          class=""
          type="inline"
          @input="handleDateInput"
          @pm="prevMonth => $emit('pm', prevMonth)"
          @nm="nextMonth => $emit('nm', nextMonth)"
          @py="prevYear => $emit('py', prevYear)"
          @ny="nextYear => $emit('ny', nextYear)"
        >
          <template v-if="!dateOnly" #footer>
            <TimePicker
              v-model="time"
              btnClass="dt-picker-time"
              :isClearable="isClearable"
              :handleClearTime="handleClearTime"
              :items="timeItems"
              :disabled-items="disabledTimes"
              :disabled="timePickerDisabled"
              :label="timeLabel"
              placeholder=""
            />
          </template>
        </DatePickerLocal>
        <div class="datepicker-action" v-if="windowWidth < 640">
          <ActionButton
            class="btn-apply"
            type="button"
            :label="null"
            :theme="buttonTheme"
            @customEvent="handleApply"
          >
            <p>Apply</p>
          </ActionButton>
          <ActionButton
            v-if="hasClear"
            class="btn-clear"
            type="button"
            :mode="'outline'"
            :label="null"
            :theme="buttonTheme"
            @customEvent="handleClear"
          >
            <p>Clear</p>
          </ActionButton>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import DatePickerLocal from "@/components/datetimepicker/DatePickerLocal";
import TimePicker from "@/components/datetimepicker/TimePicker";
import moment from "moment";
import { mixin as clickaway } from "vue-clickaway";
import ActionButton from "@/components/ActionButton";

export default {
  mixins: [clickaway],
  name: "DateTimePickerResponsive",
  components: {
    DatePickerLocal,
    TimePicker,
    ActionButton
  },
  props: {
    disabledPreviousMonth: {
      type: Boolean,
      default: false
    },
    markedDateToday: {
      type: Boolean,
      default: false
    },
    dateOnly: {
      type: Boolean,
      default: false
    },
    timeOnly: {
      type: Boolean,
      default: false
    },
    handleClearTime: {
      type: Function,
      default: () => {}
    },
    isClearable: {
      type: Boolean,
      default: false
    },
    dropdownPosition: {
      type: String,
      default: "left"
    },
    position: {
      type: String,
      default: "absolute"
    },
    label: {
      type: String,
      default: "Date"
    },
    hasLabel: {
      type: Boolean,
      default: true
    },
    timeLabel: {
      type: String,
      default: "Time"
    },
    disabledDates: {
      type: Function,
      default: () => {}
    },
    disabledTimes: {
      type: Array,
      default: () => []
    },
    timeItems: {
      type: Array,
      default: () => []
    },
    timePickerDisabled: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showToTop: [Boolean],
    value: {
      type: [Date, String],
      default: null
    },
    timeValue: {
      type: String,
      default: "0"
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    outputDate: {
      type: String,
      default: ""
    },
    withIcon: {
      type: Boolean,
      default: false
    },
    hasClear: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      open: false,
      date: null,
      time: "0",
      local: {
        dow: 0,
        hourTip: "Select Hour",
        minuteTip: "Select Minute",
        secondTip: "Select Second",
        yearSuffix: "",
        monthsHead: moment.months(),
        months: moment.monthsShort(),
        weeks: moment.weekdaysMin()
      },
      windowWidth: window.innerWidth,
      buttonTheme: {
        primary: "#20c997"
      }
    };
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener("resize", () => {
        this.windowWidth = window.innerWidth;
      });
    });
    this.handleInitialValue();
  },
  beforeDestroy() {
    window.removeEventListener("resize", () => {
      this.windowWidth = window.innerWidth;
    });
  },
  destroyed() {},
  watch: {
    date(value, oldValue) {
      if (value === oldValue) return;

      this.$emit("onDateChange", value);
      this.$emit("input", this.handleValue(value, this.time));
    },
    time(value, oldValue) {
      if (value === oldValue) return;
      this.$emit("onTimeChange", value);
      this.$emit("input", this.handleValue(this.date, value));
      this.handleDismiss();
    },
    value(newValue) {
      const timeObj = this.timeItems.find(i => i.value === this.timeValue);
      const currentDate = new Date(newValue);
      this.date = currentDate;
      const selectedTime = this.timeItems.find(
        i =>
          i.name ===
          moment(currentDate)
            .local()
            .format("h:mm A")
      );
      this.time = selectedTime ? selectedTime.value : "0";
      if (!timeObj) return;

      this.date = this.value;
      this.time = timeObj.value;
    }
  },
  computed: {
    output() {
      const dateValue = { date: "\u200B", time: "\u200B" };

      if (this.value && moment(this.value).isValid()) {
        const date = moment(this.value).local();
        dateValue.date = date.format("MMM DD, YYYY");
        dateValue.time = date.format("h:mm A");
      }

      return dateValue;
    }
  },
  methods: {
    handleInitialValue() {
      if (this.isEdit) return;
      let date = moment()
        .local()
        .startOf("date");

      if (this.value) {
        date = moment(this.value);
      }

      this.date = date.toDate();
      const timeItem = this.timeItems.find(
        i => i.name === date.format("h:mm A")
      );
      this.time = timeItem ? timeItem.value : "0";
    },
    handleValue(date, time) {
      const oldDate = moment(date).local();
      const mdy = oldDate.format("MMM DD, YYYY");
      let timeObj = this.timeItems.find(i => i.value === time);
      timeObj =
        timeObj && timeObj.disabled === false
          ? timeObj
          : this.timeItems.find(i => i.disabled === false);
      const timeString = timeObj ? timeObj.name : "12:00 AM";

      return new Date(mdy + " " + timeString).toISOString();
    },
    handleToggle(e) {
      this.open = !this.open;
      const el = document.querySelector(
        "#new-modal-booking .modalBooking-content"
      );
      if (el) {
        el.style.overflow = this.open ? "hidden auto" : "";
      }
      this.$emit("onToggle", e);
    },
    handleDismiss() {
      this.open = false;
    },
    handleClose(e) {
      this.handleDismiss();
      this.$emit("close", e);
    },
    handleClear(e) {
      this.$emit("clear", this.value);
      this.handleDismiss();
      this.$emit("onToggle", e);
    },
    handleApply(e) {
      this.$emit("apply", this.value);
      this.handleDismiss();
      this.$emit("onToggle", e);
    },
    handleDateInput(value) {
      this.$emit("input", value);
    }
  }
};
</script>

<style lang="scss" scoped>
.form-label {
  font-size: 0.75rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: #4c4c4c;
}

.dt-output {
  background-color: transparent;
  white-space: pre-line;
  border-radius: 0.25rem;
  color: #4c4c4c;
  border: 1px solid #ccc;
  display: block;
  font-size: 0.875rem;
  font-weight: 400;
  padding: 0.625rem 1.5rem;
  text-align: left;
  width: 100%;

  &:hover:not(:disabled) {
    cursor: pointer;
  }

  &:active:not(:disabled),
  &:hover:not(:disabled),
  &:focus-visible,
  &.active {
    border-color: var(--primary, $brand-primary);
  }

  &:disabled {
    color: #ccc;
  }

  &--date {
    display: flex;
    flex-direction: row;
    align-items: center;

    &__content {
      display: block;
    }

    .icon-calendar {
      margin-right: 8px;
      width: 1.042rem;
      height: 1.042rem;

      svg {
        color: #949494;
      }
    }
  }

  &-time {
    display: block;
  }
}

.dropdown {
  margin-top: 0.5rem;
  position: absolute;
  z-index: 1;

  .datepicker-header {
    padding-top: 16px;
    padding-bottom: 24px;
  }

  .dropdown-close {
    cursor: pointer;
    border: none;
    background-color: transparent;
    margin-right: 20px;

    .icon-close {
      width: 1.2rem;
      height: unset;
    }
  }

  @media screen and (max-width: 639px) {
    width: 100vw;
    height: 100vh;
    margin-top: 0;
    left: 0;
    top: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: fixed;
    background-color: #ffffff;
    z-index: 1010;

    /deep/ .datepicker {
      box-shadow: none;
      border: none;
    }

    /deep/ .datepicker-popup {
      box-shadow: none;
      border: none;
    }
  }
}

.datepicker-header {
  width: 100%;
  display: flex;
  justify-content: flex-end;
}

.datepicker-action {
  width: 248px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px 0;

  button {
    padding: 10px 16px;
    margin: 0;
    p {
      margin: 0;
    }
  }

  .btn-clear {
    margin-top: 16px;
    border: none;
  }
}

.fade-up-enter-active {
  animation: fade-up-down 0.2s;
}

.fade-up-leave-active {
  animation: fade-up-down 0.2s reverse;
}

@keyframes fade-up-down {
  0% {
    opacity: 0;
    transform: translateY(0.5rem);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/deep/ {
  .calendar {
    padding: 1rem;
  }

  .datepicker {
    background-color: #fff;
    border-radius: 0.25rem;
    box-shadow: 0px 3px 10px #00000014;
    border: 1px solid#dddddd;
    width: 18rem;

    &-popup {
      overflow: visible;
      padding: 0;
    }

    .calendar-week,
    .calendar-date {
      height: 2.1875rem;
      line-height: 2.1875rem;
      padding: 0;
      width: 2.1875rem;
    }

    .calendar-date {
      &:before {
        content: none;
      }

      &.calendar-date-selected {
        background-color: $brand-primary-v2;
      }

      &:hover {
        background-color: $brand-primary-v2;
      }
    }

    .calendar-head {
      align-items: center;
      display: flex;
      margin-top: -0.5rem;
    }

    .calendar-prev-month-btn,
    .calendar-next-month-btn {
      height: 1.5rem;
      line-height: 1.5rem;
      margin: 0;
      width: 1.5rem;
      color: #4c4c4c;
      font-size: $font-size-lg;

      &.disabled {
        pointer-events: none;
        color: #ccc;
      }

      &:hover,
      &:active {
        color: var(--primary, $brand-primary);
      }
    }

    .calendar-prev-month-btn {
      left: auto;
      right: 2rem;
    }

    .calendar-next-month-btn {
      position: relative;
    }
  }

  .dt-picker-time {
    line-height: 1.5;
    text-align: left;
    color: #4c4c4c !important;
    border-color: #ccc !important;

    &:hover {
      color: #fff !important;
      border-color: $brand-primary-v2 !important;
    }
  }

  .time {
    align-items: center;
    border-top: 2px solid #ddd;
    display: flex;
    justify-content: center;
    padding: 1rem;

    &__label {
      font-size: 0.75rem;
      font-weight: 500;
      margin-bottom: 0;
      margin-right: 1rem;
    }

    &__input-wrapper {
      margin-top: 0;
      width: 7.5rem;
    }

    &__input-wrapper__list {
      max-height: 160px;
      border-radius: 0.25rem;
      box-shadow: 0px 3px 10px #00000014;
      border: 1px solid#dddddd;
      margin: 0.5rem 0 0;
      top: 0;

      &-item {
        display: flex;
        padding: 0.25rem 0.875rem;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}
</style>
